import { useEffect, useRef, useCallback } from 'react';

/**
 * Frame-rate independent keyboard input hook
 * Works consistently across all refresh rates (60Hz, 144Hz, 165Hz, 240Hz, etc.)
 *
 * Uses requestAnimationFrame + performance.now() for precise timing, similar to useGamepad
 *
 * @param {Object} config - Configuration object
 * @param {Function} config.onKeyPress - Callback for key presses: (key) => void
 *   key: The pressed key (e.g., 'ArrowLeft', 'ArrowRight', ' ', 'q', etc.)
 * @param {Function} config.onRawInput - Callback for raw key input: ({ key }) => void
 *   key: The pressed key string
 * @param {number} config.inputDelay - Delay between key repeat in ms (default: 150)
 * @param {number} config.repeatDelay - Delay for raw input repeat (default: 150)
 * @param {boolean} config.enabled - Whether keyboard input is active (default: true)
 * @param {Array<string>} config.keys - Array of keys to listen for (default: all keys)
 *   Example: ['ArrowLeft', 'ArrowRight', 'ArrowDown', 'ArrowUp', ' ', 'q']
 * @param {boolean} config.preventDefault - Whether to prevent default behavior (default: false)
 *
 * @example
 * // Traditional usage with named callback
 * useKeyboard({
 *   onKeyPress: (key) => {
 *     if (key === 'ArrowLeft') moveLeft();
 *     if (key === 'ArrowRight') moveRight();
 *     if (key === ' ') hardDrop();
 *   },
 *   inputDelay: 150,
 *   enabled: true,
 *   keys: ['ArrowLeft', 'ArrowRight', 'ArrowDown', 'ArrowUp', ' ', 'q'],
 *   preventDefault: true
 * });
 *
 * @example
 * // Raw input with custom bindings
 * useKeyboard({
 *   onRawInput: ({ key }) => {
 *     const action = customKeyBindings[key];
 *     handleAction(action);
 *   },
 *   repeatDelay: 150,
 *   preventDefault: true
 * });
 */
export const useKeyboard = ({
  onKeyPress,
  onRawInput,
  inputDelay = 150,
  repeatDelay = 150,
  enabled = true,
  keys = null, // null means listen to all keys
  preventDefault = false
}) => {
  const animationFrameRef = useRef(null);
  const pressedKeysRef = useRef(new Set());
  const lastInputTimeRef = useRef({});
  const lastRawInputTimeRef = useRef({});

  // Track currently pressed keys
  useEffect(() => {
    const handleKeyDown = (event) => {
      // Check if we should listen to this key
      if (keys !== null && !keys.includes(event.key)) {
        return;
      }

      if (preventDefault) {
        event.preventDefault();
      }

      pressedKeysRef.current.add(event.key);
    };

    const handleKeyUp = (event) => {
      pressedKeysRef.current.delete(event.key);
    };

    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
    };
  }, [keys, preventDefault]);

  const keyboardLoop = useCallback(() => {
    if (!enabled) {
      animationFrameRef.current = requestAnimationFrame(keyboardLoop);
      return;
    }

    // Use performance.now() for high-resolution, frame-rate independent timing
    const now = performance.now();

    // Process each currently pressed key
    pressedKeysRef.current.forEach((key) => {
      // Handle raw input (for custom bindings)
      if (onRawInput) {
        const lastTime = lastRawInputTimeRef.current[key] || 0;

        if (now - lastTime >= repeatDelay) {
          onRawInput({ key });
          lastRawInputTimeRef.current[key] = now;
        }
      }

      // Handle key press (backward compatibility)
      if (onKeyPress) {
        const lastTime = lastInputTimeRef.current[key] || 0;

        if (now - lastTime >= inputDelay) {
          onKeyPress(key);
          lastInputTimeRef.current[key] = now;
        }
      }
    });

    // Continue the loop at monitor's refresh rate
    animationFrameRef.current = requestAnimationFrame(keyboardLoop);
  }, [enabled, onKeyPress, onRawInput, inputDelay, repeatDelay]);

  useEffect(() => {
    // Start the animation frame loop
    animationFrameRef.current = requestAnimationFrame(keyboardLoop);

    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
      // Clear pressed keys on cleanup
      pressedKeysRef.current.clear();
    };
  }, [keyboardLoop]);
};
